import { FormEvent, useCallback, useEffect, useState } from 'react';
import CreateUserPanel from './CreateUserPanel';
import RealmUserPicker from './RealmUserPicker';
import ProjectAccessSplitPanel from './ProjectAccessSplitPanel';
import { authClient } from '@frontend/api-clients/authClient';
import { fetchAdminProjectAccess } from '@frontend/api-clients/projectAccessClient';
import { fetchAccessAdminScope } from '@frontend/api-clients/accessAdminScopeClient';
import {
  adminAttachUserEcosystem,
  adminLookupUserByEmail,
  type RealmUserSearchResult,
} from '@frontend/api-clients/adminUserClient';
import type { AdminProjectAccessProject, User } from '@frontend/types';

interface UserAccessManagementPanelProps {
  user: User | null;
  ecosystemId: string;
  ecosystemLabel?: string;
  focusProjectId?: string | null;
  /** When true, omit create/invite (identity lives on the admin page widget). */
  hideCreateUser?: boolean;
}

export default function UserAccessManagementPanel({
  user,
  ecosystemId,
  ecosystemLabel,
  focusProjectId,
  hideCreateUser = false,
}: UserAccessManagementPanelProps) {
  const [scopeLoaded, setScopeLoaded] = useState(false);
  const [scopeError, setScopeError] = useState('');
  const [canManageRealmUsers, setCanManageRealmUsers] = useState(false);
  const [manageableProjectIds, setManageableProjectIds] = useState<string[]>([]);

  const [projects, setProjects] = useState<AdminProjectAccessProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectError, setProjectError] = useState('');
  const [projectSuccess, setProjectSuccess] = useState('');

  const [ecoAttachSelected, setEcoAttachSelected] = useState<RealmUserSearchResult | null>(null);
  const [ecoAttachQuery, setEcoAttachQuery] = useState('');
  const [ecoAttachBusy, setEcoAttachBusy] = useState(false);
  const [ecoAttachMsg, setEcoAttachMsg] = useState('');
  const [ecoPickerKey, setEcoPickerKey] = useState(0);

  const [expandedCreate, setExpandedCreate] = useState(false);
  const [expandedEcoAttach, setExpandedEcoAttach] = useState(true);
  const [expandedRoles, setExpandedRoles] = useState(true);

  const ecoTitle = ecosystemLabel || ecosystemId;

  const reloadScope = useCallback(async () => {
    const token = authClient.getToken();
    if (!token || !user) return;
    setScopeError('');
    try {
      const s = await fetchAccessAdminScope(token);
      setCanManageRealmUsers(Boolean(s.canManageRealmUsers));
      setManageableProjectIds(Array.isArray(s.manageableProjectIds) ? s.manageableProjectIds : []);
    } catch (e) {
      setScopeError(e instanceof Error ? e.message : 'Failed to load permissions');
    } finally {
      setScopeLoaded(true);
    }
  }, [user]);

  const loadProjectAccess = useCallback(async () => {
    const token = authClient.getToken();
    if (!token || !ecosystemId) return;
    setLoadingProjects(true);
    setProjectError('');
    try {
      const loadedProjects = await fetchAdminProjectAccess(ecosystemId, token);
      setProjects(loadedProjects);
    } catch (loadErr) {
      // Keep the list empty so Add project remains usable when create is allowed.
      setProjects([]);
      setProjectError(loadErr instanceof Error ? loadErr.message : 'Failed to load project access');
    } finally {
      setLoadingProjects(false);
    }
  }, [ecosystemId]);

  useEffect(() => {
    reloadScope();
  }, [reloadScope]);

  useEffect(() => {
    if (!scopeLoaded || scopeError) return;
    if (!canManageRealmUsers && manageableProjectIds.length === 0) return;
    loadProjectAccess();
  }, [scopeLoaded, scopeError, canManageRealmUsers, manageableProjectIds.length, loadProjectAccess]);

  const visible =
    scopeLoaded &&
    !scopeError &&
    (canManageRealmUsers || manageableProjectIds.length > 0) &&
    (!focusProjectId || canManageRealmUsers || manageableProjectIds.includes(focusProjectId));

  if (!user || !ecosystemId) return null;

  if (!scopeLoaded) {
    return (
      <section className="tree-dashboard-panel ecosystem-card admin-widget-card" aria-busy="true">
        <p className="tree-dashboard-muted">Loading access tools…</p>
      </section>
    );
  }

  if (scopeError) {
    return (
      <section className="tree-dashboard-panel ecosystem-card admin-widget-card">
        <h3 className="tree-dashboard-section-title">Access management</h3>
        <p className="settings-message settings-error">{scopeError}</p>
      </section>
    );
  }

  if (!visible) return null;

  const onProvisionContinue = () => {
    setExpandedEcoAttach(true);
    setExpandedRoles(true);
    setProjectSuccess('Continue below to attach ecosystem membership or assign project roles.');
    void loadProjectAccess();
  };

  const attachEcosystem = async (event: FormEvent) => {
    event.preventDefault();
    setEcoAttachMsg('');
    setEcoAttachBusy(true);

    try {
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');

      let userId = ecoAttachSelected?.userId || '';
      let label = ecoAttachSelected
        ? `${ecoAttachSelected.email || ecoAttachSelected.username} (${ecoAttachSelected.username})`
        : '';

      if (!userId) {
        const typed = ecoAttachQuery.trim().toLowerCase();
        if (!typed.includes('@')) {
          throw new Error('Select a realm user from the list, or type their full email.');
        }
        const lookedUp = await adminLookupUserByEmail(token, typed);
        userId = lookedUp.userId;
        label = lookedUp.email || typed;
      }

      await adminAttachUserEcosystem(token, userId, ecosystemId);
      setEcoAttachMsg(`Attached ${label} to ecosystem ${ecoTitle}.`);
      setEcoAttachSelected(null);
      setEcoAttachQuery('');
      setEcoPickerKey((k) => k + 1);
      await loadProjectAccess();
    } catch (e) {
      setEcoAttachMsg(e instanceof Error ? e.message : 'Failed to attach ecosystem membership');
    } finally {
      setEcoAttachBusy(false);
    }
  };

  return (
    <section className="tree-dashboard-panel ecosystem-card admin-widget-card" aria-label="Access management">
      <h3 className="tree-dashboard-section-title">Access management</h3>
      <p className="tree-dashboard-muted">
        Invite or create users, attach ecosystem membership, assign portal project roles, and review downstream projection
        status ({ecoTitle}).
      </p>

      {!hideCreateUser && canManageRealmUsers ? (
        <CreateUserPanel
          expanded={expandedCreate}
          onToggle={() => setExpandedCreate((v) => !v)}
          onProvisionSuccess={onProvisionContinue}
          ecosystemAttachMode="scoped"
          scopedEcosystemId={ecosystemId}
        />
      ) : null}

      <div className="ecosystem-card admin-widget-card" style={{ marginTop: '1rem' }}>
        <button type="button" className="admin-widget-header" onClick={() => setExpandedEcoAttach((v) => !v)}>
          <div className="admin-widget-header-main">
            <h4 className="ecosystem-title">Ecosystem membership</h4>
            <span className="admin-status-badge status-unknown">Keycloak group</span>
          </div>
          <span className="admin-widget-toggle">{expandedEcoAttach ? '▾' : '▸'}</span>
        </button>
        {expandedEcoAttach ? (
          <div className="admin-widget-body">
            <p className="ecosystem-description">
              Search existing realm users and attach them to <strong>{ecoTitle}</strong>. Project role grants can
              auto-attach when enabled. New accounts still use Create / Invite on the Identity widget.
            </p>
            <form className="admin-member-form admin-member-form-picker" onSubmit={attachEcosystem}>
              <RealmUserPicker
                key={ecoPickerKey}
                id="eco-attach-user-search"
                label="Find realm user"
                value={ecoAttachSelected}
                disabled={ecoAttachBusy}
                allowEmailFallback
                onChange={(user, query) => {
                  setEcoAttachSelected(user);
                  setEcoAttachQuery(query);
                  setEcoAttachMsg('');
                }}
              />
              <button type="submit" className="btn btn-secondary" disabled={ecoAttachBusy}>
                {ecoAttachBusy ? 'Attaching…' : 'Attach to ecosystem'}
              </button>
            </form>
            {ecoAttachMsg ? (
              <p
                className={
                  ecoAttachMsg.includes('Failed') || ecoAttachMsg.includes('Select a realm')
                    ? 'settings-message settings-error'
                    : 'settings-message settings-success'
                }
              >
                {ecoAttachMsg}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="ecosystem-card admin-widget-card" style={{ marginTop: '1rem' }}>
        <button type="button" className="admin-widget-header" onClick={() => setExpandedRoles((v) => !v)}>
          <div className="admin-widget-header-main">
            <h4 className="ecosystem-title">Project roles &amp; sync</h4>
            <span className="admin-status-badge status-unknown">portal SoT</span>
          </div>
          <span className="admin-widget-toggle">{expandedRoles ? '▾' : '▸'}</span>
        </button>
        {expandedRoles ? (
          <div className="admin-widget-body">
            {projectError ? <div className="settings-message settings-error">{projectError}</div> : null}
            {projectSuccess ? <div className="settings-message settings-success">{projectSuccess}</div> : null}
            <ProjectAccessSplitPanel
              ecosystemId={ecosystemId}
              projects={projects}
              loading={loadingProjects}
              focusProjectId={focusProjectId}
              onReload={loadProjectAccess}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
